import { createBrowserRouter } from 'react-router-dom'
import { AppLayout } from '@/layouts/AppLayout'
import { SupplierLayout } from '@/layouts/SupplierLayout'
import { ProtectedRoute } from './ProtectedRoute'
import { RouteErrorBoundary } from './RouteErrorBoundary'
import { LoginPage } from '@/features/auth/LoginPage'

// Todas as páginas são carregadas via `lazy` do react-router — cada uma fica
// no seu próprio chunk, por isso o staff não descarrega o portal do
// fornecedor nem vice-versa, e o primeiro carregamento só traz a página
// pedida. Layouts e ProtectedRoute ficam no bundle principal (são pequenos
// e necessários em todas as rotas).

export const router = createBrowserRouter([
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    element: <ProtectedRoute />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <AppLayout />,
        children: [
          {
            index: true,
            path: '/',
            lazy: async () => {
              const { Navigate } = await import('react-router-dom')
              return { Component: () => <Navigate to="/command-center" replace /> }
            },
          },
          {
            path: '/command-center',
            lazy: async () => ({ Component: (await import('@/features/command-center/CommandCenterPage')).CommandCenterPage }),
          },
          {
            path: '/dashboard',
            lazy: async () => ({ Component: (await import('@/features/dashboard/DashboardPage')).DashboardPage }),
          },
          {
            path: '/insights',
            lazy: async () => ({ Component: (await import('@/features/insights/InsightsPage')).InsightsPage }),
          },
          {
            path: '/damages',
            lazy: async () => ({ Component: (await import('@/features/damages/DamagesPage')).DamagesPage }),
          },
          {
            // Static child, so it wins over /damages/:damageId below —
            // react-router ranks static segments above dynamic ones.
            path: '/damages/archive',
            lazy: async () => ({ Component: (await import('@/features/damages/ArchivePage')).ArchivePage }),
          },
          {
            // Deep link to one damage. The panel reads the document by id
            // (useDamage), so it opens cases the list itself would not show —
            // a fleet-scoped user following a link, or anything outside the
            // list's window. A static child such as /damages/archive still
            // wins over this, react-router ranks static segments first.
            path: '/damages/:damageId',
            lazy: async () => ({ Component: (await import('@/features/damages/DamagesPage')).DamagesPage }),
          },
          {
            path: '/trailers',
            lazy: async () => ({ Component: (await import('@/features/trailers/TrailersPage')).TrailersPage }),
          },
          {
            path: '/assistance',
            lazy: async () => ({ Component: (await import('@/features/assistance/AssistancePage')).AssistancePage }),
          },
          {
            path: '/suppliers',
            lazy: async () => ({ Component: (await import('@/features/suppliers/SuppliersPage')).SuppliersPage }),
          },
          {
            path: '/invoices',
            element: <ProtectedRoute minimumRole="fleet_manager" />,
            children: [{
              index: true,
              lazy: async () => ({ Component: (await import('@/features/invoices/InvoicesPage')).InvoicesPage }),
            }],
          },
          {
            path: '/compliance',
            lazy: async () => ({ Component: (await import('@/features/compliance/CompliancePage')).CompliancePage }),
          },
          {
            path: '/maintenance',
            lazy: async () => ({ Component: (await import('@/features/maintenance/MaintenancePage')).MaintenancePage }),
          },
          {
            path: '/costs',
            lazy: async () => ({ Component: (await import('@/features/costs/CostsPage')).CostsPage }),
          },
          {
            path: '/files',
            lazy: async () => ({ Component: (await import('@/features/files/FilesPage')).FilesPage }),
          },
          {
            // Every action on this page (approve, reject, escalate) requires
            // fleet_manager server-side, and the estimates collection group it
            // reads is not fleet-scoped — so it is not shown to the scoped
            // roles that would see other fleets' quotes in it.
            path: '/approvals',
            element: <ProtectedRoute minimumRole="fleet_manager" />,
            children: [{
              index: true,
              lazy: async () => ({ Component: (await import('@/features/approvals/ApprovalsPage')).ApprovalsPage }),
            }],
          },
          {
            path: '/settings',
            lazy: async () => {
              const { Navigate } = await import('react-router-dom')
              return { Component: () => <Navigate to="/settings/users" replace /> }
            },
          },
          {
            path: '/settings/users',
            element: <ProtectedRoute minimumRole="tenant_admin" />,
            children: [{
              index: true,
              lazy: async () => ({ Component: (await import('@/features/settings/users/UsersPage')).UsersPage }),
            }],
          },
          {
            // Bases (fleets). fleet_manager, not tenant_admin: managing which
            // base a trailer belongs to is fleet work, and the rules already
            // draw the line there (`allow write: if isFleetManager`).
            path: '/settings/fleets',
            element: <ProtectedRoute minimumRole="fleet_manager" />,
            children: [{
              index: true,
              lazy: async () => ({ Component: (await import('@/features/settings/fleets/FleetsPage')).FleetsPage }),
            }],
          },
          {
            path: '/settings/notifications',
            element: <ProtectedRoute minimumRole="tenant_admin" />,
            children: [{
              index: true,
              lazy: async () => ({ Component: (await import('@/features/settings/notifications/NotificationSettingsPage')).NotificationSettingsPage }),
            }],
          },
          {
            path: '/settings/emails',
            element: <ProtectedRoute minimumRole="tenant_admin" />,
            children: [{
              index: true,
              lazy: async () => ({ Component: (await import('@/features/settings/emails/EmailLogsPage')).EmailLogsPage }),
            }],
          },
          {
            path: '/settings/auto-approval',
            element: <ProtectedRoute minimumRole="tenant_admin" />,
            children: [{
              index: true,
              lazy: async () => ({ Component: (await import('@/features/settings/auto-approval/AutoApprovalSettingsPage')).AutoApprovalSettingsPage }),
            }],
          },
        ],
      },
    ],
  },
  {
    element: <ProtectedRoute requiredRole="supplier" />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <SupplierLayout />,
        children: [
          {
            path: '/supplier-portal',
            lazy: async () => {
              const { Navigate } = await import('react-router-dom')
              return { Component: () => <Navigate to="/supplier-portal/damages" replace /> }
            },
          },
          {
            path: '/supplier-portal/damages',
            lazy: async () => ({ Component: (await import('@/features/supplier-portal/SupplierPortalPage')).SupplierPortalPage }),
          },
          {
            path: '/supplier-portal/maintenance',
            lazy: async () => ({ Component: (await import('@/features/supplier-portal/SupplierPortalPage')).SupplierPortalPage }),
          },
          {
            path: '/supplier-portal/invoices',
            lazy: async () => ({ Component: (await import('@/features/supplier-portal/SupplierPortalPage')).SupplierPortalPage }),
          },
        ],
      },
    ],
  },
  {
    path: '/unauthorized',
    lazy: async () => {
      const { EmptyState } = await import('@fleetops/ui')
      const { ShieldOff } = await import('lucide-react')
      return {
        Component: () => (
          <div className="flex h-screen items-center justify-center">
            <EmptyState
              icon={<ShieldOff className="h-10 w-10" />}
              title="Acesso Negado"
              description="Não tem permissão para ver esta página."
            />
          </div>
        ),
      }
    },
  },
  {
    path: '*',
    lazy: async () => {
      const { EmptyState } = await import('@fleetops/ui')
      return {
        Component: () => (
          <div className="flex h-screen items-center justify-center">
            <EmptyState title="Página não encontrada" description="Esta página não existe." />
          </div>
        ),
      }
    },
  },
])
